import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import { RoleModel } from "../factories/role/role.model";

export type GetRolesParams = {
  orgId: string;
};

export type AddRolePayload = {
  roleName: string;
  orgId: string;
  createdByUserId: string;
  userIds: string[];
};

export type UpdateRolePayload = RoleModel;

export type DeleteRolePayload = {
  roleId: string;
};

export type AddUserToRolePayload = {
  roleId: string;
  userId: string;
};

export type AddUsersToRolePayload = AddUserToRolePayload[];

export type RemoveUsersFromRolePayload = string[];

export const roleSlice = createApi({
  reducerPath: "roleSlice",
  tagTypes: ["Role"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getRoles: build.query<RoleModel[], GetRolesParams>({
      query: ({ orgId }) => ({
        url: "api/roles/get-all/",
        params: { orgId },
        method: "GET",
      }),
      providesTags: ["Role"],
    }),
    addRole: build.mutation<unknown, AddRolePayload>({
      query: (body) => ({
        url: "api/roles/add/",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Role"],
    }),
    updateRole: build.mutation<unknown, UpdateRolePayload>({
      query: (body) => ({
        url: "api/roles/update/",
        body,
        method: "PUT",
      }),
      invalidatesTags: ["Role"],
    }),
    deleteRole: build.mutation<unknown, DeleteRolePayload>({
      query: ({ roleId }) => ({
        url: "api/roles/",
        params: { roleId },
        method: "DELETE",
      }),
      invalidatesTags: ["Role"],
    }),
    addUserToRole: build.mutation<unknown, AddUserToRolePayload>({
      query: (body) => ({
        url: "api/user-roles/add/",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Role"],
    }),
    addUsersToRole: build.mutation<unknown, AddUsersToRolePayload>({
      query: (body) => ({
        url: "api/user-roles/add-multiple/",
        body,
        method: "POST",
      }),
      invalidatesTags: ["Role"],
    }),
    removeUsersFromRole: build.mutation<unknown, RemoveUsersFromRolePayload>({
      query: (body) => ({
        url: "api/user-roles/",
        body,
        method: "DELETE",
      }),
      invalidatesTags: ["Role"],
    }),
  }),
});
export const {
  useGetRolesQuery,
  useAddRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAddUserToRoleMutation,
  useAddUsersToRoleMutation,
  useRemoveUsersFromRoleMutation,
} = roleSlice;
